const SPREAD_CROP_START = 0.53;
const SAMPLE_WIDTH = 24;
const SAMPLE_HEIGHT = 32;
const MAX_MEAN_CHANNEL_DIFFERENCE = 0.14;

const detectionCache = new Map<string, Promise<boolean>>();

type CoverImageSet = {
  shelf: string | null;
  shelfHigh: string | null;
  detail: string | null;
  samples: string[];
};

export type CaseImageSources = {
  frontCandidates: string[];
  back: string | null;
  spine: string | null;
};

export function selectCaseImageSources(images: CoverImageSet, hasSpread: boolean): CaseImageSources {
  if (hasSpread && images.detail) {
    return { frontCandidates: [images.detail], back: images.detail, spine: images.detail };
  }
  return {
    // On the FANZA product page, `imageURL.large` is shown first and the API's
    // `sample_l[0]` follows it as the second image. Do not skip sample_l[0]:
    // for a non-splittable large jacket it is the intended front, while
    // sample_l[1] is the intended back.
    frontCandidates: [images.samples[0], images.shelfHigh, images.shelf]
      .filter((source): source is string => Boolean(source)),
    back: images.samples[1] ?? null,
    spine: null,
  };
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image comparison failed"));
    image.src = source;
  });
}

export function meanChannelDifference(a: Uint8ClampedArray, b: Uint8ClampedArray): number {
  if (a.length !== b.length || a.length === 0) return 1;
  let total = 0;
  let channels = 0;
  for (let index = 0; index < a.length; index += 4) {
    total += Math.abs(a[index] - b[index]);
    total += Math.abs(a[index + 1] - b[index + 1]);
    total += Math.abs(a[index + 2] - b[index + 2]);
    channels += 3;
  }
  return total / (channels * 255);
}

/**
 * DMM does not expose cover-layout metadata. Compare the right side of the
 * large artwork with the API's known portrait cover. A real back/spine/front
 * spread closely matches; a single landscape artwork does not.
 */
export function detectCompositeCover(detailSource: string, portraitSource: string): Promise<boolean> {
  const key = `${detailSource}\n${portraitSource}`;
  const cached = detectionCache.get(key);
  if (cached) return cached;

  const detection = Promise.all([loadImage(detailSource), loadImage(portraitSource)])
    .then(([detail, portrait]) => {
      if (detail.width <= detail.height * 1.08 || portrait.height <= portrait.width * 1.08) return false;
      const canvas = document.createElement("canvas");
      canvas.width = SAMPLE_WIDTH * 2;
      canvas.height = SAMPLE_HEIGHT;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return false;

      const cropX = Math.round(detail.width * SPREAD_CROP_START);
      context.drawImage(
        detail,
        cropX,
        0,
        detail.width - cropX,
        detail.height,
        0,
        0,
        SAMPLE_WIDTH,
        SAMPLE_HEIGHT,
      );
      context.drawImage(portrait, SAMPLE_WIDTH, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
      const cropPixels = context.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT).data;
      const portraitPixels = context.getImageData(SAMPLE_WIDTH, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT).data;
      return meanChannelDifference(cropPixels, portraitPixels) <= MAX_MEAN_CHANNEL_DIFFERENCE;
    })
    .catch(() => false);

  detectionCache.set(key, detection);
  return detection;
}
